import {
    actionMapWords,
    type Blockers,
    blockedReason,
    type CapacityBlocker,
    type DecisionGate,
    type KpiHealthRow,
    type RunRow,
    stoppedReason,
} from '@/api'
import { AGENT_PROVIDER_NAMES, AGENT_PROVIDERS, AGENT_SUBSCRIPTIONS, hasSubscription } from '@/lib/agents'

export type BlockerSection =
    | 'models'
    | 'capacity'
    | 'lostConnections'
    | 'raised'
    | 'taskProblems'
    | 'teamHealth'
    | 'escalations'
    | 'stopped'
    | 'blocked'
    | 'failed'
    | 'stalled'
    | 'paused'
    | 'plan'

/** Runs the CEO's spend cap paused. The API lists them in `paused` beside plan-paused runs. */
export function spendPausedRuns(blockers: Blockers): RunRow[] {
    return (blockers.paused ?? []).filter(run => run.status !== 'paused_plan')
}

/**
 * Runs a plan limit paused (#445). Listed whether or not Spend is on: the cap is the CEO's, the plan is the plan.
 */
export function planPausedRuns(blockers: Blockers): RunRow[] {
    return (blockers.paused ?? []).filter(run => run.status === 'paused_plan')
}

/** How a plan limit's unit reads in a sentence. */
const PLAN_UNIT_WORDS: Record<string, string> = {
    portal_run: 'portal runs this month',
    runner_minute: 'workspace runner minutes this month',
}

/**
 * Why a plan-paused run is waiting, from the limit it recorded (`plan_limit: portal_run 100 of 100 on Free`), and
 * that it comes back by itself. Null for any other run.
 */
export function planPausedReason(run: RunRow): string | null {
    if (run.status !== 'paused_plan') {
        return null
    }
    const met = /^plan_limit: (\w+) (\d+) of (\S+) on (.+)$/.exec(run.blockedReason ?? '')
    const limit = met
        ? `The ${met[4]} plan's limit is reached: ${met[2]} of ${met[3]} ${PLAN_UNIT_WORDS[met[1] ?? ''] ?? met[1]} used`
        : 'A plan limit is reached'
    return `${limit}. It resumes by itself after an upgrade or when the month rolls over.`
}

export function escalationRuns(blockers: Blockers): RunRow[] {
    return (blockers.blocked ?? []).filter(run => run.blockClass === 'escalation')
}

export function autoBlockedRuns(blockers: Blockers): RunRow[] {
    return (blockers.blocked ?? []).filter(run => run.blockClass !== 'escalation')
}

/**
 * The KPI rows Blockers lists. A KPI that is only AMBER or RED (`light`) is left out: the Enterprise page and its nav
 * item show how the KPIs stand, so Blockers keeps to a KPI someone must fix (no key KPI, no target, overdue for a
 * reading, or a proposal waiting on the CEO).
 */
export function blockerKpiRows(blockers: Blockers): BlockerKpiRow[] {
    return (blockers.kpiHealth ?? []).filter((row): row is BlockerKpiRow => row.condition !== 'light')
}

/** A KPI row Blockers lists: any condition but the light alone. */
export type BlockerKpiRow = Omit<KpiHealthRow, 'condition'> & { condition: Exclude<KpiHealthRow['condition'], 'light'> }

/**
 * Alerts per Blockers section, one entry per section the page shows. The paged section counts every row, not the
 * page. The sidebar badge is `blockerAlertTotal`, so the page headers always add up to it.
 */
export function blockerCounts(blockers: Blockers): Record<BlockerSection, number> {
    return {
        models: blockers.models?.length ?? 0,
        // One entry per exhausted connection — the enterprise has no LLM capacity, which is one condition,
        // not one blocker per run that could not get any (#659).
        capacity: blockers.capacity?.length ?? 0,
        // One per subscription connection that was dropped or revoked, until it is reconnected (#1719).
        lostConnections: blockers.lostConnections?.length ?? 0,
        // #431: one per finding sent to another team's function that no single team could take, until cleared.
        raised: blockers.raised?.length ?? 0,
        taskProblems: blockers.taskProblems?.length ?? 0,
        // A team that has skipped its weekly retro is team health too, not a run blocker (os#373), and so is a
        // KPI that needs attention (#899), its light aside. The deprecated `missingMetric` list is not counted.
        teamHealth:
            (blockers.missingLead?.length ?? 0) +
            (blockers.overdueRetro?.length ?? 0) +
            blockerKpiRows(blockers).length,
        escalations: escalationRuns(blockers).length,
        stopped: blockers.stopped?.length ?? 0,
        blocked: autoBlockedRuns(blockers).length,
        failed: blockers.failedTotal ?? blockers.failed.length,
        stalled: blockers.stalled.length,
        paused: blockers.spendEnabled === false ? 0 : spendPausedRuns(blockers).length,
        // Counted with Spend off too: a plan limit is not the CEO's to switch off (#445).
        plan: planPausedRuns(blockers).length,
    }
}

export function blockerAlertTotal(blockers: Blockers): number {
    return Object.values(blockerCounts(blockers)).reduce((sum, count) => sum + count, 0)
}

/** Last 1-based page for `total` rows. */
export function lastPage(total: number, pageSize: number): number {
    return Math.max(1, Math.ceil(total / Math.max(1, pageSize)))
}

/** A run row on Blockers: the API names its task, the teammate it worked as and its team (#1772). */
export type BlockerRun = RunRow

/** A capacity entry on Blockers: which limit ran out, its own label, when it resets and the connection's own name. */
export type CapacityLimit = CapacityBlocker

/**
 * One fix a Blockers row offers: a link to where the fix is made (`to`), as Raise cap goes to Spend, or an action
 * the page performs on the run itself (`action`).
 */
export type BlockerFix = { label: string; to?: string; action?: 'retry' | 'cancel' }

/**
 * A run row in words (#1772): the task, the teammate it worked as (or its team), one plain sentence saying what
 * happened, an optional second line with the detail behind it, and the fixes, in the order the row offers them.
 */
export type BlockerMessage = {
    task: string | null
    teammate: string | null
    sentence: string
    detail: string | null
    fix: BlockerFix[]
    /**
     * #957: on Blockers, what an `action_map` row reads instead of `sentence` and `detail`: the unmapped-action notice
     * naming the connection. The sentence keeps the run's own wording for anywhere the notice is not drawn.
     */
    notice?: { sentence: string; detail: string | null }
}

/** What a run row says beyond who it is about: the sentence, its detail and the fixes. */
type RunWords = Omit<BlockerMessage, 'task' | 'teammate'>

const RETRY: BlockerFix = { label: 'Retry', action: 'retry' }
const CANCEL: BlockerFix = { label: 'Cancel', action: 'cancel' }

/** Where a connection's limit is raised or its credits are added: the provider page. */
const PROVIDER_PAGE = '/agents'

/** What a limit says when the provider reported no usable reset: today's wording, never a made-up date (#659). */
const NO_RESET = 'at an unknown time — the connection reported no reset'

/**
 * What a lost subscription connection's row says happened (#1719): who stopped accepting which login. A row that
 * names no provider is a Claude one, the only kind there was before the others.
 */
export function lostLogin(provider: string | undefined): string {
    const known = AGENT_PROVIDERS.find(candidate => candidate === (provider ?? 'claude'))
    if (!known || !hasSubscription(known)) {
        return 'This AI account login is no longer accepted'
    }
    const { vendor, plan } = AGENT_SUBSCRIPTIONS[known]
    return `${vendor} no longer accepts this ${plan} login`
}

/**
 * What a blocked connection's row says about it beside its name (#624, PR #754): the AI service as people know it and
 * who the connection belongs to, the company as a whole or one person, in plain words rather than its layer.
 */
export function blockedConnectionWhose(connection: { provider?: string; layer?: string; ownerName: string }): string {
    const service = providerName(connection.provider ?? 'claude')
    return connection.layer === 'member'
        ? `${service}, ${connection.ownerName}’s own connection`
        : `${service}, shared by everyone at ${connection.ownerName}`
}

/** A provider as people know it ("OpenRouter"), or as the API sent it when the portal does not know it. */
function providerName(provider: string): string {
    const known = AGENT_PROVIDERS.find(candidate => candidate === provider)
    return known ? AGENT_PROVIDER_NAMES[known] : provider
}

/**
 * When a limit resets, in UTC — the instant the provider itself reports, so the page never shifts a reset to the
 * reader's local day and says work resumes on the wrong date (#659). Null when no usable time was reported.
 */
function resetTime(resetsAt: string | null | undefined): string | null {
    const at = resetsAt ? new Date(resetsAt) : null
    if (!at || Number.isNaN(at.getTime())) {
        return null
    }
    return `${at.toISOString().slice(0, 16).replace('T', ' ')} UTC`
}

/** What is known about a limit that ran out: from a capacity entry, or only its kind and reset from a run's reason. */
type LimitFacts = Pick<CapacityBlocker, 'kind' | 'label' | 'period' | 'resetsAt'> & { provider?: string }

/**
 * Which limit is used up and when it comes back, by kind: a rate limit names its window and says runs park until it
 * resets; a spend limit names its period (the key's, on OpenRouter) and its reset; a balance does not reset. A
 * missing kind is a rate limit: what a connection out of capacity always meant before kinds (#1772).
 */
function usedUp(limit: LimitFacts): string {
    const at = resetTime(limit.resetsAt) ?? NO_RESET
    if (limit.kind === 'balance') {
        return 'the balance is used up. It does not reset: add credits.'
    }
    if (limit.kind === 'spend_limit') {
        const noun = limit.period ? `${limit.period} limit` : 'spend limit'
        let owner = 'the'
        if (limit.provider === 'openrouter') {
            owner = "the key's"
        } else if (limit.label) {
            owner = `the ${limit.label}`
        }
        return `${owner} ${noun} is used up. It resets ${at}.`
    }
    const window = limit.label ? `${limit.label} rate limit` : 'rate limit'
    return `the ${window} is used up. It resets ${at}; runs park until then.`
}

/** The fix for a limit that ran out: raise a spend limit, or add credits, on the provider page. A rate limit waits. */
function limitFix(kind: CapacityBlocker['kind']): BlockerFix | null {
    if (kind === 'spend_limit') {
        return { label: 'Raise limit', to: PROVIDER_PAGE }
    }
    if (kind === 'balance') {
        return { label: 'Add credits', to: PROVIDER_PAGE }
    }
    return null
}

/**
 * A capacity entry as one plain sentence and its fix (#1772): the provider and the connection's own name, which limit
 * is used up — a rate limit, a spend limit with its period, or a balance — and when it resets, or that it does not
 * and what to do. Raise limit and Add credits go to the provider page, where both are done.
 */
export function capacityMessage(entry: CapacityLimit): { sentence: string; fix: BlockerFix | null } {
    const provider = providerName(entry.provider)
    const name = entry.connectionName?.trim()
    const who = name ? `${provider} · ${name}` : provider
    return { sentence: `${who}: ${usedUp(entry)}`, fix: limitFix(entry.kind) }
}

/**
 * The kind and reset a run's no-capacity reason carries: `llm_quota_exhausted[:<resetsAt>][ kind=<kind>]`. A reason
 * recorded before kinds, or carrying none, is a rate limit. Null for any other reason.
 */
function quotaReason(reason: string | null | undefined): Pick<LimitFacts, 'kind' | 'resetsAt'> | null {
    const met = /^llm_quota_exhausted(?::(\S*))?(?:\s+kind=(\w+))?$/.exec(reason?.trim() ?? '')
    if (!met) {
        return null
    }
    const kind = met[2] === 'spend_limit' || met[2] === 'balance' ? met[2] : 'rate_limit'
    return { kind, resetsAt: met[1] || null }
}

/** A run out of LLM capacity, as the capacity panel says it, and the fix for that limit after the run's own. */
function capacityRun(subject: string, quota: Pick<LimitFacts, 'kind' | 'resetsAt'>, own: BlockerFix[]): RunWords {
    const fix = limitFix(quota.kind)
    return {
        sentence: `${subject} is out of LLM capacity: ${usedUp(quota)}`,
        detail: null,
        fix: fix ? [...own, fix] : own,
    }
}

/**
 * A failure code in plain words, for the codes the OS records when a run fails. A code it does not know is shown
 * as it came, after a plain lead-in, so a new kind of failure is never hidden.
 */
const FAILURE_WORDS = new Map<string, string>([
    ['agent_gave_up', 'it gave up before finishing'],
    ['git_push_missing', 'it finished without pushing its work'],
    ['grant_denied', 'it was refused a permission it asked for'],
    ['gate_required_for_tool', 'it called a tool that needs an approval gate first'],
    ['llm_empty_response', 'the model sent back an empty reply'],
    ['llm_max_steps', 'it used every step it was allowed without finishing'],
    ['llm_not_bound', 'no LLM connection is bound for it'],
    ['missing_task_version', 'its task version is missing'],
    ['run_stalled', 'it made no progress within the stall window'],
    ['tool_auth_denied', 'a tool refused its credentials'],
    ['tool_not_bound', 'a tool it needs is not connected'],
    ['tool_not_bound_or_scope', 'a tool it needs is not connected, or lacks the access it asked for'],
    ['tool_not_on_task', 'it called a tool its task does not list'],
    ['worker_lost', 'the worker running it was lost'],
])

/** A failed run (in Failed, or heading a stopped pipeline): why, in plain words, and Retry. */
function failedRun(run: BlockerRun, subject: string): RunWords {
    const reason = run.failedReason?.trim()
    const quota = quotaReason(reason)
    if (quota) {
        return capacityRun(subject, quota, [RETRY])
    }
    if (!reason) {
        return { sentence: `${subject} stopped without recording why.`, detail: null, fix: [RETRY] }
    }
    const words = FAILURE_WORDS.get(reason.split(':')[0] ?? '')
    const sentence = words ? `${subject} failed: ${words} (${reason}).` : `${subject} stopped and reported: ${reason}`
    return { sentence, detail: null, fix: [RETRY] }
}

/** How an auto-blocked run reads by its block class: what it waits on, and how it comes back. */
function blockedWords(run: BlockerRun, reason: string): string {
    if (/^mcp_http_\d+:builtin(:|$)/.test(reason)) {
        return `the OS's own MCP server refused a call (${reason}). It resumes by itself.`
    }
    switch (run.blockClass) {
        case 'model':
            return `its model is unavailable (${reason}). It resumes once the model answers or the tier is pointed at another model.`
        case 'credentials':
            return `a credential was refused or is missing (${reason}). It resumes once the credential is rebound.`
        case 'action_map': {
            // Not a credential: the connection's token is fine, and its action map on Tools is what lacks the actions.
            const words = actionMapWords(reason)
            return `${words.charAt(0).toLowerCase()}${words.slice(1)} It resumes once that connection's action map names them.`
        }
        case 'definition':
            return `its task definition cannot run as written (${reason}). It resumes after the task is edited.`
        case 'infra':
            return `a service it relies on failed (${reason}). It resumes by itself.`
        default:
            return `it reported ${reason}. It resumes by itself.`
    }
}

const ACTION_UNMAPPED = 'tool_action_unmapped:'

/**
 * #957: the `<slot>.<action>` names a run parked on `tool_action_unmapped:<slot.action,…>` waits on, each once and
 * split at the slot key, or null for a run parked on anything else. Every name comes from the block itself.
 */
export function unmappedActions(run: BlockerRun): Array<{ slot: string; action: string }> | null {
    const reason = run.blockedReason?.trim()
    if (run.status !== 'blocked' || !reason?.startsWith(ACTION_UNMAPPED)) return null
    const names = [
        ...new Set(
            reason
                .slice(ACTION_UNMAPPED.length)
                .split(',')
                .map(name => name.trim())
                .filter(Boolean),
        ),
    ]
    return names.map(name => {
        const dot = name.indexOf('.')
        return dot < 0 ? { slot: '', action: name } : { slot: name.slice(0, dot), action: name.slice(dot + 1) }
    })
}

/** `a`, `a and b`, `a, b and c`. */
function listed(items: readonly string[]): string {
    const rest = items.slice(0, -1)
    return rest.length ? `${rest.join(', ')} and ${items[items.length - 1]}` : items.join('')
}

/**
 * #957: a run parked because the connection filling a slot does not say which of its tools an action is. It names the
 * connection (the binding filling the slot on the run's cascade, from the block) and the actions, and its one fix is
 * Map it: that connection's editor at its Actions section, or the Tools page naming the slot when no connection was
 * resolved. No Retry: once the map names the action the API starts the run again, and a retry before would only
 * park again.
 */
function actionUnmappedNotice(
    run: BlockerRun,
    subject: string,
    unmapped: Array<{ slot: string; action: string }>,
): { sentence: string; detail: string } {
    const actions = listed(unmapped.map(entry => entry.action))
    const verb = unmapped.length === 1 ? 'is' : 'are'
    const slots = listed([...new Set(unmapped.map(entry => entry.slot).filter(Boolean))])
    const binding = run.blockedBinding
    const connection = binding?.name?.trim()
        ? `The ${binding.name.trim()} connection`
        : `The connection filling the ${slots || 'tool'} slot`
    return {
        sentence: `${connection} doesn't say which of its tools ${actions} ${verb}.`,
        detail: `${subject} waits until it does: map ${unmapped.length === 1 ? 'it' : 'them'} on the connection's Actions, and the run carries on with nothing else to do.`,
    }
}

/** Map it: the resolved connection's editor at its Actions section, or the Tools page when none was resolved. */
function mapIt(run: BlockerRun): BlockerFix {
    const id = run.blockedBinding?.id
    return { label: 'Map it', to: id ? `/tools?actions=${encodeURIComponent(id)}` : '/tools' }
}

function actionUnmappedRun(run: BlockerRun, subject: string, unmapped: Array<{ slot: string; action: string }>) {
    return { ...actionUnmappedNotice(run, subject, unmapped), fix: [mapIt(run)] } satisfies RunWords
}

/** A run parked on something outside the task: what it waits on in plain words, with Retry and Cancel as today. */
function blockedRun(run: BlockerRun, subject: string): RunWords {
    const unmapped = unmappedActions(run)
    // The block's own reason decides the notice (#957), whichever class it came with: `action_map` since #1214, the
    // class it had before otherwise. An `action_map` row keeps its own sentence below and carries the notice beside it.
    if (unmapped?.length && run.blockClass !== 'action_map') {
        return actionUnmappedRun(run, subject, unmapped)
    }
    const reason = run.blockedReason?.trim()
    const quota = quotaReason(reason)
    if (quota) {
        return capacityRun(subject, quota, [RETRY, CANCEL])
    }
    const sentence = reason ? `${subject} is waiting: ${blockedWords(run, reason)}` : `${subject} is waiting.`
    if (run.blockClass === 'action_map') {
        // Mapped on the connection's Actions, the run resumes by itself, so Map it and Cancel and no Retry (#957).
        const notice = unmapped?.length ? { notice: actionUnmappedNotice(run, subject, unmapped) } : {}
        return { sentence, detail: null, fix: [mapIt(run), CANCEL], ...notice }
    }
    return { sentence, detail: null, fix: [RETRY, CANCEL] }
}

/**
 * A run that needs a decision: a loop cap, a question to the CEO, or another escalation it recorded. Any other
 * escalation reads as needing a human decision, never as its raw code. When a gate holds the decision, the row says
 * what it asks and links to the run whose page decides it, ahead of Retry and Cancel.
 */
function escalatedRun(run: BlockerRun, subject: string, gate: DecisionGate | null): RunWords {
    const reason = run.blockedReason?.trim() ?? ''
    const decide: BlockerFix[] = gate ? [{ label: 'Decide', to: `/runs/${gate.runId}` }] : []
    const fix = [...decide, RETRY, CANCEL]
    if (reason.startsWith('loop_cap')) {
        // The cap's own reading says which task ran how many times, so the decision can be made from here.
        return { sentence: `${subject} went round its loop cap without finishing.`, detail: blockedReason(run), fix }
    }
    if (reason.startsWith('ceo_wait_')) {
        return { sentence: `${subject} is waiting on the CEO's answer.`, detail: null, fix }
    }
    const asks = gate?.title?.trim()
    let detail = `${subject} stopped for a person. No gate is open for it yet.`
    if (gate) {
        detail = asks ? `${subject}: ${asks}` : subject
    }
    return { sentence: 'Needs a human decision', detail, fix }
}

/**
 * A stopped pipeline: a run that finished on an outcome nothing follows, which needs its next step chosen on its
 * task's page, or a failed run nothing picked up again.
 */
function stoppedRun(run: BlockerRun, subject: string): RunWords {
    const deadEnd = stoppedReason(run)
    if (!deadEnd) {
        return failedRun(run, subject)
    }
    return {
        sentence: `${subject} finished, but nothing was set to run next.`,
        detail: deadEnd,
        fix: [{ label: 'Choose what runs next', to: `/tasks/${run.taskId}` }],
    }
}

/** What happened to a run in this section, and what fixes it. */
function whatHappened(section: BlockerSection, run: BlockerRun, subject: string, gate: DecisionGate | null): RunWords {
    switch (section) {
        case 'stopped':
            return stoppedRun(run, subject)
        case 'escalations':
            return escalatedRun(run, subject, gate)
        case 'blocked':
            return blockedRun(run, subject)
        case 'stalled':
            return { sentence: `${subject} made no progress within the stall window.`, detail: null, fix: [CANCEL] }
        case 'paused':
            return {
                sentence: `${subject} is paused: the spend cap is reached.`,
                detail: null,
                fix: [{ label: 'Raise cap', to: '/spend' }],
            }
        case 'plan':
            return {
                sentence: `${subject} is paused. ${planPausedReason(run) ?? 'A plan limit is reached.'}`,
                detail: null,
                fix: [{ label: 'Upgrade', to: '/billing#plans' }],
            }
        default:
            return failedRun(run, subject)
    }
}

/**
 * A run row in a Blockers section as who, what happened and what to do about it (#1772): the task, the teammate it
 * worked as (its team when it worked as no member, nobody when it had neither), one plain sentence that never leaves
 * a raw code on its own, and at least one fix. Fixes go where the fix is made — the task's page to choose what runs
 * next, Spend to raise the cap, Billing to upgrade, the provider page for a limit, the run that holds the gate an
 * escalation waits on (`gate`, from the response's `decisionGates`) — or are Retry and Cancel.
 */
export function runBlockerMessage(
    section: BlockerSection,
    run: BlockerRun,
    gate: DecisionGate | null = null,
): BlockerMessage {
    const task = run.taskName?.trim() || null
    const teammate = run.assigneeName?.trim() || run.teamName?.trim() || null
    return { task, teammate, ...whatHappened(section, run, task ?? 'This run', gate) }
}
